/* global globalEvents */
import appState from 'snippets/cart-global/@store'
import { createContext, Fragment } from 'preact'
import { eventProps } from 'helpers/global'
import { select, loadScript } from 'helpers/dom'
import { initBasketTracking } from 'uses/useSearchspringTracking'
import { updateCart, openMiniCart, validatePreOrder, validateStoreOnly, hasProductInStore, validateInStoreOnly, validatePralineBoxMixedLocations, validateMixedCart, klaviyoTrackingAddToCart } from 'helpers/cart'
import { useContext, useEffect, useMemo, useState } from 'preact/hooks'
import MiniCartList from 'snippets/mini-cart-list/mini-cart-list'
import MiniCartRelated from 'snippets/mini-cart-related/mini-cart-related'
import MiniCartButton from 'snippets/mini-cart-button/mini-cart-button'
import CartGiftMessage from 'snippets/cart-gift-message/cart-gift-message'
import LoaderSpin from 'snippets/loader-spin/loader-spin'
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock'

const selectors = {
  miniCartContentSelector: '.js-mini-cart-content',
  miniCartOpenedSelector: '.modal-dialog-mini-cart.modal-dialog-opened'
}

const cartMinimumSettings = window.SLS_STATE.cart.minimum

const AppState = createContext()

function MiniCart(props) {
  return (
    <AppState.Provider value={appState()}>
      <App slot={props} />
    </AppState.Provider>
  )
}

function App(props) {
  const state = useContext(AppState)

  const [isLoaded, setIsLoaded] = useState(false)
  const [isPreOrderError, setIsPreOrderError] = useState(false)
  const [isStoreOnlyError, setIsStoreOnlyError] = useState(false)
  const [hiddenGiftMessage, setHiddenGiftMessage] = useState(false)
  const [isInStoreOnlyError, setIsInStoreOnlyError] = useState(false)
  const [isPralineBoxMixedLocationsError, setIsPralineBoxMixedLocationsError] = useState(false)
  const [isMixedCartError, setIsMixedCartError] = useState(false)

  const cart = useMemo(() => state.cart.value, [state.cart.value])
  const fetching = useMemo(() => state.fetching.value, [state.fetching.value])
  const hasAgeVerification = useMemo(() => state.hasAgeVerification.value, [state.hasAgeVerification.value])

  const classContent = useMemo(() => {
    let classContent = 'mini-cart-content'
    if (hasAgeVerification) {
      classContent = 'mini-cart-content-has-age'
    }
    if (isPreOrderError || isStoreOnlyError) {
      classContent = 'mini-cart-content-pre-order-error'
    }
    if (hasAgeVerification && (isPreOrderError || isStoreOnlyError)) {
      classContent = 'mini-cart-content-pre-order-age'
    }
    return classContent
  }, [hasAgeVerification, isPreOrderError, isStoreOnlyError])

  const init = () => {
    if (!isLoaded) {
      setIsLoaded(true)
    }
  }

  const initSherpa = () => {
    try {
      loadScript(`https://plugin-app.sherpa.net.au/js/widget.js?shop=${window.SLS_STATE.shopify.shop}.myshopify.com`, true, false)
    } catch (error) {
      console.log('error', error)
    }
  }

  useEffect(() => {
    setTimeout(() => {
      globalEvents.on(eventProps.cart.add, async () => {
        const newCart = await updateCart()
        state.setCart(newCart)
        openMiniCart()
        init()
        initBasketTracking(newCart)
        setTimeout(() => initSherpa(), 600)
        klaviyoTrackingAddToCart(newCart)
      })
      globalEvents.on(eventProps.cart.update, async (newCart) => {
        state.setCart(newCart)
      })
      globalEvents.on(eventProps.cart.render, () => {
        init()
        !isLoaded && setTimeout(() => initSherpa(), 600)
      })
      globalEvents.on(eventProps.cart.clear, async () => {
        const newCart = await updateCart()
        state.setCart(newCart)
        init()
      })
    }, 500)
  }, [])

  useEffect(() => {
    const status = validatePreOrder(cart)
    setIsPreOrderError(status)

    const statusStoreOnly = validateStoreOnly(cart)
    setIsStoreOnlyError(statusStoreOnly)

    const statusHiddenGiftMessage = hasProductInStore(cart)
    setHiddenGiftMessage(statusHiddenGiftMessage)

    const statusInStoreOnly = validateInStoreOnly(cart)
    setIsInStoreOnlyError(statusInStoreOnly)

    const statusPralineBoxMixedLocationsError = validatePralineBoxMixedLocations(cart)
    setIsPralineBoxMixedLocationsError(statusPralineBoxMixedLocationsError)

    const statusMixedCartError = validateMixedCart(cart)
    setIsMixedCartError(statusMixedCartError)
  }, [cart])

  return (
    <div className="relative flex flex-col h-[calc(100%-53px)] md:h-[calc(100%-69px)]">
      <form action="/cart" method="POST" id="cart" className={`${classContent}  js-mini-cart-content overflow-y-auto overflow-x-hidden scrollbar`}>
        {cart && cart.items.length ? (
          isLoaded ? (
            <Fragment>
              <CartGiftMessage
                classWrapper="flex-col !m-4 !p-4"
                classButton="w-full mt-3 hover:bg-secondary-hover hover:border-secondary-hover hover:text-white"
                cart={cart}
                hiddenGiftMessage={hiddenGiftMessage}
              />
              <MiniCartList items={cart.items} setCart={state.setCart} setFetching={state.setFetching} fetching={fetching} hiddenGiftMessage={hiddenGiftMessage} />
              <MiniCartButton cart={cart} fetching={fetching} hasAgeVerification={hasAgeVerification} isPreOrderError={isPreOrderError} isStoreOnlyError={isStoreOnlyError} isInStoreOnlyError={isInStoreOnlyError} isPralineBoxMixedLocationsError={isPralineBoxMixedLocationsError} isMixedCartError={isMixedCartError} />
            </Fragment>
          ) : (
            <div className="flex items-center justify-center">
              <LoaderSpin width="10" height="10" />
            </div>
          )
        ) : (
          <div className="container col-span-2 bg-white">
            <div class="py-12 md:py-20 xl:py-10 text-center">
              <h1 className="mt-4 text-xl font-bold text-center text-grey-900">Your bag is empty</h1>
              <p className="mt-2 mb-4 md:mt-3 md:mb-6">Your shopping bag is empty, click below to continue shopping.</p>
              <a href="/collections/all" class="button-primary">
                Continue shopping
              </a>
            </div>
          </div>
        )}
      </form>
    </div>
  )
}

export default MiniCart
